import { Injectable } from "@angular/core";
import { Http, Response } from "@angular/http";
import {
    ITCIApplication,
    ITCIApplicationAuditRecord,
    SERVICE_URLS,
    QUERY_PARAMS,
    TCI_TENANT_SERVICE_PROVIDER_TYPE,
    MapAppType,
    TCITenantSelectorType,
    TCITenantAppType,
    TCITenantAppDetailsMenu,
    TCI_APP_DETAIL_MENU_TYPE,
} from "@tci/common";
import { ITCIAppDetailsMenuOutput } from "../../ng-tenant/tci-tenant-components";

import { Subject, Observable } from "rxjs";
import * as lodash from "lodash";

export interface ITCIAppRefreshNotification {
    app: ITCIApplication;
    lastAudit: ITCIApplicationAuditRecord;
    success: boolean;
    source: {
        selector?: TCITenantSelectorType,
        provider?: TCI_TENANT_SERVICE_PROVIDER_TYPE
    };
}

@Injectable()
export class NgAppChangeNotificationService {
    private static instance: NgAppChangeNotificationService;

    private refreshSubj$: Subject<ITCIAppRefreshNotification> = new Subject();

    private includeAudits = {};

    // Store getAppById shared requests
    private getAppByIdRequests: { [key: string]: Observable<ITCIApplication> } = {};

    // Refresh notification will be sent for these HM actions
    private reactEvents: string[];

    public static getInstance(http: Http): NgAppChangeNotificationService {
        if (!NgAppChangeNotificationService.instance) {
            NgAppChangeNotificationService.instance = new NgAppChangeNotificationService(http);
        }
        return NgAppChangeNotificationService.instance;
    }

    constructor(private http: Http) {
        this.reactEvents = TCITenantAppDetailsMenu.getAllMenuItems(TCI_APP_DETAIL_MENU_TYPE.HAMBURGER_MENU)
            .map(evnt => evnt.getComponentSelectorType().toString());
        console.log(`NgAppChangeNotificationService Instance created: ${new Date().toLocaleString()}`);
    }

    setAuditsData(app: ITCIApplication) {
        this.includeAudits[app.id] = true;
    }

    unsetAuditsData(app: ITCIApplication) {
        this.includeAudits[app.id] = false;
    }

    getRefreshSubject() {
        return this.refreshSubj$;
    }

    getLastUpdateInfo(app: ITCIApplication): Observable<ITCIAppRefreshNotification> {
        const url = `${SERVICE_URLS.APPS}/audits/${app.id}?${QUERY_PARAMS.LAST_AUDIT}=true&${QUERY_PARAMS.APP_TYPE}=${app.type.toString()}`;
        return this.http.get(url)
            .map(res => res.json() || null)
            .catch(error => {
                console.log(error);
                throw error;
            })
            .map(res => {
                if (res.app) {
                    return { app: MapAppType(res.app), lastAudit: res.lastAudit, success: true };
                } else {
                    console.warn("Refresh response doesn't have app instance. Original app returned instead", "Response >", res, "App requested >", app);
                    res.app = app;
                    res.success = false;
                    return res;
                }
            });
    }

    evaluateHMOutput(app: ITCIApplication, hmOutput: ITCIAppDetailsMenuOutput) {
        if (hmOutput.refresh && this.reactEvents.includes(hmOutput.menuItemSelector.toString())) {
            this.requestRefresh(app, hmOutput);
        }
    }

    requestRefresh(app: ITCIApplication, hmOutput?: ITCIAppDetailsMenuOutput, provider?: TCI_TENANT_SERVICE_PROVIDER_TYPE) {
        const isProviderAppDelete = provider === TCI_TENANT_SERVICE_PROVIDER_TYPE.APP_DELETE || false;
        const isProviderAppCreate = provider === TCI_TENANT_SERVICE_PROVIDER_TYPE.APP_CREATE || false;

        const cb = (refr: ITCIAppRefreshNotification) => {
            if (!lodash.isEqual(app, refr.app) || isProviderAppDelete || isProviderAppCreate) { // Update the app info
                Object.assign(app, refr.app);

                // Set source type
                refr.source = { provider };
                if (hmOutput) {
                    refr.source.selector = hmOutput.menuItemSelector;
                }

                this.refreshSubj$.next(refr);
            }
        };

        if (this.includeAudits[app.id]) {
            this.getLastUpdateInfo(app)
                .subscribe(refr => cb(refr));
        } else if (isProviderAppDelete) {
            Observable.of({ app, lastAudit: null, success: true, source: null })
                .subscribe(refr => cb(refr));
        } else {
            this.getAppById(app.id, app.type)
                .map(res => ({ app: res, lastAudit: null, success: true, source: null }))
                .subscribe(refr => cb(refr));
        }
    }

    public getAppById(appId: string, appType?: TCITenantAppType): Observable<ITCIApplication> {
        if (!this.getAppByIdRequests[appId]) {
            const queryParam: string = appType.toString() ? `?${QUERY_PARAMS.APP_TYPE}=${appType.toString()}` : "";
            this.getAppByIdRequests[appId] = this.http.get(`${SERVICE_URLS.APPS}/${appId}${queryParam}`)
                .map((response: Response) => {
                    const res = MapAppType(response.json());
                    delete this.getAppByIdRequests[appId];
                    return res;
                })
                .share();
        }

        return this.getAppByIdRequests[appId];
    }
}
